import { IMaintenanceEventFilters } from '@/pages/Calendar/calendar.types'
import calendarUtils from '@/pages/Calendar/calendar.utils'
import { IUser } from '@/pages/Users/users.types'
import usePersistentState from '@/shared/hooks/usePersistentState'
import { useLayoutEffect, useMemo, useState } from 'react'
import { TSortDirection } from '../../UsersSortTypeDropdown'
import userUtils from '@/pages/User/user.utils'
import timeUtils from '@/utils/time.utils'
import { TReportViewingMode } from './useReportLayout'

const useReportCtrl = (viewingMode: TReportViewingMode, users?: IUser[]) => {
  const [usersSortType, setUsersSortType] = useState<TSortDirection>('asc')
  const [hoverCol, setHoverCol] = useState<number | null>(null)

  const [persistenceFilters, setPersistenceFilters] = usePersistentState<IMaintenanceEventFilters>(
    calendarUtils.getIntervalFromDate(viewingMode),
    'organizationUsersReport/filters',
    'inMemory'
  )

  const sortedUsers = useMemo(() => {
    if (!users || users.length === 0) return []
    const direction = usersSortType === 'asc' ? 1 : -1
    return users.toSorted(
      (a, b) => direction * userUtils.getFullName(a.userInfo).localeCompare(userUtils.getFullName(b.userInfo))
    )
  }, [users, usersSortType])

  const daysRange = useMemo(() => {
    return timeUtils.eachDayOfInterval({ start: persistenceFilters.date_gte, end: persistenceFilters.date_lte })
  }, [persistenceFilters])

  useLayoutEffect(() => {
    const dayCount = timeUtils.differenceInCalendarDays(persistenceFilters.date_lte, persistenceFilters.date_gte) + 1
    if (dayCount % 7 !== 0) return
    if (viewingMode === 'fourWeek' && dayCount < 28) {
      setPersistenceFilters((prev) => ({
        date_gte: prev.date_gte,
        date_lte: timeUtils.addWeeks(prev.date_lte, 4 - dayCount / 7),
      }))
    } else if (viewingMode === 'twoWeek' && dayCount > 14) {
      setPersistenceFilters((prev) => ({
        date_gte: prev.date_gte,
        date_lte: timeUtils.subWeeks(prev.date_lte, dayCount / 7 - 2),
      }))
    }
  }, [viewingMode])

  return {
    data: {
      filters: persistenceFilters,
      daysRange,
      sortedUsers,
      usersSortType,
      hoverCol,
    },
    handlers: {
      handleDateChange: (dates: IMaintenanceEventFilters) => setPersistenceFilters(dates),
      handleTodayClick: () => setPersistenceFilters(calendarUtils.getIntervalFromDate(viewingMode)),
      handleUsersSortTypeChange: (value: TSortDirection) => setUsersSortType(value),
      handleHoverColChange: (value: number | null) => setHoverCol(value),
    },
  }
}

export default useReportCtrl
